import React, { createContext, useContext, useState, useEffect } from 'react';
import { Drug, drugs as initialDrugs } from '../data/drugs';

interface DrugsContextType {
  drugs: Drug[];
  addCustomDrug: (drug: Omit<Drug, 'id' | 'isCustom'>) => Drug;
  importCustomDrug: (drug: Drug) => void;
  resetDrugs: () => void;
}

const DrugsContext = createContext<DrugsContextType | null>(null);

export const DrugsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Load custom drugs from session storage
  const loadCustomDrugs = (): Drug[] => {
    try {
      const savedCustomDrugs = localStorage.getItem('custom-drugs');
      if (savedCustomDrugs) {
        return JSON.parse(savedCustomDrugs);
      }
    } catch (error) {
      console.error('Error loading custom drugs:', error);
    }
    return [];
  };

  const [drugs, setDrugs] = useState<Drug[]>(() => {
    const customDrugs = loadCustomDrugs();
    return [...initialDrugs, ...customDrugs];
  });

  const addCustomDrug = (drugData: Omit<Drug, 'id' | 'isCustom'>): Drug => {
    const id = `custom-${Date.now()}`;
    const newDrug: Drug = {
      ...drugData,
      id,
      isCustom: true
    };

    // Update drugs state
    const updatedDrugs = [...drugs, newDrug];
    setDrugs(updatedDrugs);
    
    // Save custom drugs to session storage immediately
    const customDrugs = updatedDrugs.filter(drug => drug.isCustom);
    localStorage.setItem('custom-drugs', JSON.stringify(customDrugs));
    
    return newDrug;
  };

  // Used for importing - preserves the original ID
  const importCustomDrug = (drug: Drug): void => {
    // Make sure drug has isCustom flag
    const customDrug: Drug = {
      ...drug,
      isCustom: true
    };
    
    // Check if we already have this drug to avoid duplicates
    if (!drugs.some(d => d.id === customDrug.id)) {
      // Update drugs state with the exact drug object (preserving ID)
      const updatedDrugs = [...drugs, customDrug];
      setDrugs(updatedDrugs);
      
      // Save to session storage
      const customDrugs = updatedDrugs.filter(drug => drug.isCustom);
      localStorage.setItem('custom-drugs', JSON.stringify(customDrugs));
    }
  };

  const resetDrugs = () => {
    setDrugs(initialDrugs);
    localStorage.removeItem('custom-drugs');
  };

  // Save custom drugs whenever drugs state changes
  useEffect(() => {
    const customDrugs = drugs.filter(drug => drug.isCustom);
    if (customDrugs.length > 0) {
      localStorage.setItem('custom-drugs', JSON.stringify(customDrugs));
    }
  }, [drugs]);

  return (
    <DrugsContext.Provider value={{ drugs, addCustomDrug, importCustomDrug, resetDrugs }}>
      {children}
    </DrugsContext.Provider>
  );
};

export const useDrugs = () => {
  const context = useContext(DrugsContext);
  if (!context) {
    throw new Error('useDrugs must be used within a DrugsProvider');
  }
  return context;
}; 